import type {
    IGeneratePaymentMethodsReportDataCmd,
    IGenerateSalesReportDataCmd,
} from "@/data/commands/order/GenerateSalesReportDataCmd";
import { Factory } from "@/data/dao/Factory";
import { DaoError, type IDataSource, type IPaginatedResult } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { OrderDao } from "@/data/dao/service/OrderDao";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";
import type { IOffer } from "@/data/model/Offer";
import type { IPaymentMethodsReportData } from "@/data/model/PaymentMethodsReportData";
import type { ISalesReportData } from "@/data/model/SalesReportData";
import type { IPaginatedQry } from "@/data/queries/PaginatedQry";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";
import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";

import { BaseModal } from "@/shared/elements/BaseModal";
import { clientLanguage, clientLocale } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";

import { Popup } from "@/components/dialog/Popup";

import { OperatorClient } from "@/operator/OperatorClient";
import { SelectBootstrap } from "@/operator/elements/SelectBootstrap";
import { trans } from "@/operator/localization/Trans";
import { DatePicker } from "@/operator/ui/DatePicker";
import { TimePicker } from "@/operator/ui/TimePicker";
import type { IReportFilterEntityBase } from "@/data/model/ReportFilterEntityBase";
import type { IReportFilterOptionsQry } from "@/data/queries/location/ReportFilterOptionsQry";
import { exceptionTypes } from "@/data/util/Config";


export class SalesReportsOptionsModal extends BaseModal {
    private $form: JQuery;
    private $submitBtn: JQuery;

    private userSelect: SelectBootstrap<IReportFilterEntityBase>;
    private roomSelect: SelectBootstrap<IReportFilterEntityBase>;
    private offerSelect: SelectBootstrap<IOffer>;
    private fulfillmentGroupSelect: SelectBootstrap<IFulfillmentGroup>;
    private fromDatePicker: DatePicker;
    private fromTimePicker: TimePicker;
    private toDatePicker: DatePicker;
    private toTimePicker: TimePicker;
    private paymentMethodsReportSwitch: JQuery;
    private abortController?: AbortController;

    private usersDataSource: IDataSource<IReportFilterEntityBase> = {
        getData: async (filter: IReportFilterOptionsQry) => {
            filter.locationId = OperatorClient.context.settings!.location!.id;
            if (filter.locationId == null) {
                const error = new Error("Location id is null");
                logError(error);
                throw error;
            }

            try {
                const items = await Factory.getInstance<LocationDao>(LocationDao).getLocationUserFilterOptions(
                    filter.locationId
                );
                
                const locationUserOption: IPaginatedResult<IReportFilterEntityBase> = {
                    limit: items.length,
                    items: items,
                    hasNext: false,
                    hasPrev: false,
                    offset: 0,
                };

                return locationUserOption;
            } catch (ex) {
                logError(ex);
                throw ex;
            }
        },
    };

    private roomsDataSource: IDataSource<IReportFilterEntityBase> = {
        getData: async (filter: IReportFilterOptionsQry) => {
            filter.locationId = OperatorClient.context.settings!.location!.id;
            if (filter.locationId == null) {
                const error = new Error("Location id is null");
                logError(error);
                throw error;
            }

            try {
                const items = await Factory.getInstance<LocationDao>(LocationDao).getLocationRoomFilterOptions(
                    filter.locationId
                );

                const locationRoomOptions: IPaginatedResult<IReportFilterEntityBase> = {
                    limit: items.length,
                    items: items,
                    hasNext: false,
                    hasPrev: false,
                    offset: 0,
                };

                return locationRoomOptions;
            } catch (ex) {
                logError(ex);
                throw ex;
            }
        },
    };

    constructor(
        onClickExport: (data: ISalesReportData) => void,
        onClickExportPaymentMethod: (data: IPaymentMethodsReportData) => void
    ) {
        super(trans.salesReports.title, "modal-lg", false, false);
        this.$element.addClass("salesexport");
        this.$element.addClass("scrollable");
        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");

        const usersGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                trans.general.entities.users +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");

        const roomGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                trans.general.entities.rooms +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");

        this.userSelect = new SelectBootstrap<IReportFilterEntityBase>(
            undefined,
            this.usersDataSource,
            (c) => {
                if (c.id === "endUsers") {
                    return trans.salesReports.endUsers;
                }

                if (c.id === "all") {
                    return trans.general.all;
                }

                return c.name;
            },
            false,
            undefined,
            undefined,
            false,
            undefined,
            undefined,
            undefined
        ).renderTo(usersGroup);

        this.roomSelect = new SelectBootstrap<IReportFilterEntityBase>(
            undefined,
            this.roomsDataSource,
            (c) => {
                return c.name;
            },
            false,
            trans.general.all,
            undefined,
            true,
            undefined,
            undefined,
            undefined
        ).renderTo(roomGroup);

        const offerGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                trans.general.entities.offers +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        this.offerSelect = new SelectBootstrap<IOffer>(
            undefined,
            {
                getData(filter: IPaginatedQry, abortSignal?: AbortSignal): Promise<IPaginatedResult<IOffer>> {
                    return Factory.getInstance<CatalogDao>(CatalogDao).getOffers(
                        {
                            includeChildOffers: true,
                            ...filter,
                        },
                        abortSignal
                    );
                },
            },
            (c) => c.name,
            false,
            trans.general.all,
            undefined,
            true
        ).renderTo(offerGroup);

        const fulfillmentGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                trans.general.entities.fulfillmentGroups +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");

        this.fulfillmentGroupSelect = new SelectBootstrap<IFulfillmentGroup>(
            undefined,
            {
                getData(
                    filter: IPaginatedQry,
                    abortSignal?: AbortSignal
                ): Promise<IPaginatedResult<IFulfillmentGroup>> {
                    return Factory.getInstance<CatalogDao>(CatalogDao).getFulfillmentGroups(
                        {
                            includeFromChildLocations: true,
                            ...filter,
                        },
                        abortSignal
                    );
                },
            },

            ((c) => Format.htmlEscape(getNameInLocationLanguage(c, true))) as any,
            false,
            trans.general.all,
            undefined,
            true,
            "internalName",
            (items) => {
                return items
                    .sort((item1, item2) => {
                        const item1Name = Format.translate(
                            item1.name,
                            OperatorClient.context.settings!.location!.language
                        );
                        const item2Name = Format.translate(
                            item2.name,
                            OperatorClient.context.settings!.location!.language
                        );
                        return item1Name > item2Name ? 1 : -1;
                    })
                    .sort((item1, item2) => {
                        const order1 = item1.order ?? 999999999;
                        const order2 = item2.order ?? 999999999;

                        if (order1 === order2) {
                            return 0;
                        }

                        if (order1 > order2) return 1;

                        return -1;
                    });
            }
        ).renderTo(fulfillmentGroup);

        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        const midDay = DateTime.todayAsUtcMidnight();

        const fromGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                trans.general.timeFormat.start +
                ' <span class="mandatory">*</span></label><div class="col-xl-3 date"></div><div class="col-xl-3 time"></div></div>'
        ).appendTo(createSection);

        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 3);

        this.fromDatePicker = new DatePicker(undefined, {
            startDate: startDate,
            endDate: endOfDay,
            language: clientLocale(),
        })
            .setValue(midDay)
            .renderTo(fromGroup.find("> .col-xl-3.date"));

        this.fromTimePicker = new TimePicker(undefined, {
            minuteStep: 1,
            secondStep: 1,
            maxHours: 24,
            defaultTime: "00:00:00",
        }).renderTo(fromGroup.find("> .col-xl-3.time"));

        const toGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                trans.general.timeFormat.end +
                ' <span class="mandatory">*</span></label><div class="col-xl-3 date"></div><div class="col-xl-3 time"></div></div>'
        ).appendTo(createSection);

        this.toDatePicker = new DatePicker(undefined, {
            startDate: startDate,
            endDate: endOfDay,
            language: clientLocale(),
        }).renderTo(toGroup.find("> .col-xl-3.date"));

        this.toTimePicker = new TimePicker(undefined, {
            minuteStep: 1,
            secondStep: 1,
            maxHours: 24,
            defaultTime: "current",
        }).renderTo(toGroup.find("> .col-xl-3.time"));

        const viewSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");

        viewSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                trans.salesReportExport.specialReports +
                "</h3></div></div>"
        );

        const paymentMethodsReportSwitchGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label"></label><div class="col-xl-6" /></div>'
        )
            .appendTo(viewSection)
            .find("> .col-xl-6");

        this.paymentMethodsReportSwitch = $(
            '<label class="payment-methods-label"><input type="checkbox" class="form-control" /> ' +
                trans.salesReportExport.paymentMethodsReport +
                "</label>"
        )
            .appendTo(paymentMethodsReportSwitchGroup)
            .find("> input");

        const footer = $('<div class="modal-footer" />').insertAfter(this.$body);

        $('<button type="button" class="btn btn-secondary">' + trans.general.cancel + "</button>")
            .appendTo(footer)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + trans.general.display + "</button>")
            .appendTo(footer)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit(async (e) => {
            e.preventDefault();
            e.stopPropagation();

            const selectedUserOption = this.userSelect.getModel();
            const from = this.fromDatePicker.getValue();
            const to = this.toDatePicker.getValue();

            let hasError = false;
            this.$form.find(".is-invalid").removeClass("is-invalid");

            if (from === undefined) {
                this.fromDatePicker.setError(true);
                hasError = true;
            } else {
                let startTime = this.fromTimePicker.getSeconds();
                if (startTime === undefined) startTime = 0;

                from.setSeconds(startTime + from.getTimezoneOffset() * 60);
            }

            if (to === undefined) {
                this.toDatePicker.setError(true);
                hasError = true;
            } else {
                let endTime = this.toTimePicker.getSeconds();
                if (endTime === undefined) endTime = 0;

                to.setSeconds(endTime + to.getTimezoneOffset() * 60);
            }

            if (from! >= to!) {
                this.fromDatePicker.setError(true);
                this.fromTimePicker.setError(true);
                this.toDatePicker.setError(true);
                this.toTimePicker.setError(true);
                hasError = true;
            }

            if (hasError) return;

            const paymentMethodsReportSelected = this.paymentMethodsReportSwitch.is(":checked");
            this.$submitBtn.addClass("disabled").prop("disabled", true).addClass("loading");

            if (paymentMethodsReportSelected) {
                const data: IGeneratePaymentMethodsReportDataCmd = {
                    roomIds: this.roomSelect.getValues(),
                    offerIds: this.offerSelect.getValues(),
                    fulfillmentGroupIds: this.fulfillmentGroupSelect.getValues(),
                    startDateTime: from,
                    endDateTime: to,
                    waiterId: selectedUserOption?.id === "all" || selectedUserOption?.id === "endUsers" ? undefined : selectedUserOption?.id,
                    filterByEndUsers: selectedUserOption != null && selectedUserOption.id === "endUsers",
                    hideZeroQtyPaymentMethods: true,
                    language: clientLanguage(),
                };

                try {
                    const abortController = new AbortController();
                    this.abortController = abortController;
                    
                    const reportData = await Factory.getInstance<OrderDao>(OrderDao).generatePaymentMethodsReportData(data, abortController.signal);
                    onClickExportPaymentMethod(reportData);
                } catch (ex) {
                    logError(ex);

                    const error = ex as DaoError;

                    if (error?.additionalData?.reason === exceptionTypes.unauthorizedAccessToNonPortalReportException) {
                        Popup.show({
                            title: trans.general.failed,
                            message: trans.salesReports.noAccessToReports,
                            uniqueId: "operation-failed-popup",
                        });
                    }
                    else {
                        Popup.show({
                            title: trans.general.failed,
                            message: trans.general.operationFailed,
                            uniqueId: "operation-failed-popup",
                        });
                    }
                } finally {
                    this.$submitBtn.removeClass("disabled").prop("disabled", false).removeClass("loading");
                    this.close();
                }
            } else {
                const data: IGenerateSalesReportDataCmd = {
                    roomIds: this.roomSelect.getValues(),
                    offerIds: this.offerSelect.getValues(),
                    fulfillmentGroupIds: this.fulfillmentGroupSelect.getValues(),
                    from: from,
                    to: to,
                    userId: selectedUserOption?.id === "all" || selectedUserOption?.id === "endUsers" ? undefined : selectedUserOption?.id,
                    filterByEndUsers: selectedUserOption != null && selectedUserOption.id === "endUsers",
                };

                try {
                    const abortController = new AbortController();
                    this.abortController = abortController;
                    
                    const reportData = await Factory.getInstance<OrderDao>(OrderDao).generateSalesReportData(data, abortController.signal);
                    onClickExport(reportData);
                } catch (ex) {
                    logError(ex);
                    const error = ex as DaoError;
                    
                    if (error?.additionalData?.reason === exceptionTypes.unauthorizedAccessToNonPortalReportException) {
                        Popup.show({
                            title: trans.general.failed,
                            message: trans.salesReports.noAccessToReports,
                            uniqueId: "operation-failed-popup",
                        });
                    } 
                    else {
                        Popup.show({
                            title: trans.general.failed,
                            message: trans.general.operationFailed,
                            uniqueId: "operation-failed-popup",
                        });
                    }
                } finally {
                    this.$submitBtn.removeClass("disabled").prop("disabled", false).removeClass("loading");
                    this.close();
                }
            }

            return false;
        });

        this.toDatePicker.setValue(midDay).setDisabled(false);
        this.toTimePicker.setValue(DateTime.toTimeString(new Date(), true)).setDisabled(false);
    }

    protected onShow() {}

    protected onHide() {
        this.abortController?.abort();
    }
}
